<template>
	<Features v-bind="featuresProps" @show-wizard="showWizard" :key="key" />
</template>

<script>
import { AccountPermissions, ProjectPermissions } from '@ply-io/ply-front-shared-utilities';

import { FeatureType } from '@/components/CreationWizard/Steps/ChooseProjectAndFeatureType/FeatureTypeSelector/feature-type-selector.const';
import { TOOL_PLACEMENTS } from '@/components/CreationWizard/Steps/ChooseToolPlacement/ChooseToolPlacement.const.js';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { hasAccountPermission } from '@/store/modules/account/getters';
import { getServiceBySlug } from '@/store/modules/apps/getters';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { selectedProjectId } from '@/store/modules/selectedProject/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import AnalyticsService from '@/utils/AnalyticsService';
import { VIRTUAL_PROJECT_IDS } from '@/utils/projects.const';
import Features from '@/views/AppContentPages/Features.vue';
import CreateAndManageService from '@/views/AppLayout/ManageAndCreate/create-and-manage-service.js';
import { UNIDENTIFIED_SERVICE_WORKSPACE_ID } from '@/views/AppLayout/ManageAndCreate/create-and-manage.const.js';
export default {
	name: 'PlacementFeaturesDataProvider',
	props: {
		title: { type: String },
		subTitle: { type: String },
		idsToShow: { type: Array },
		featureType: { type: String },
		serviceSlug: { type: String },
		initialState: { type: Object },
		page: { type: Number, default: 1 },
		serviceWorkspaceId: { type: String },
		showButtonDropdowns: { type: Boolean },
		disableCreateNew: { type: Boolean, default: false },
	},
	components: { Features },
	computed: {
		...mapGettersShorthand({ getServiceBySlug, hasAccountPermission, selectedProjectId, projectHasPermission }),
		featuresProps() {
			return {
				page: this.page,
				title: this.title,
				subTitle: this.subTitle,
				filterBy: this.filterBy,
				idsToShow: this.idsToShow,
				notSupported: this.notSupported,
				canCreateNew: this.canCreateNew,
				titlePrefixIcon: this.titlePrefixIcon,
				showButtonDropdowns: this.showButtonDropdowns,
			};
		},
		key() {
			return (this.appId || 'waiting-for-service') + JSON.stringify(this.featuresProps);
		},
		titlePrefixIcon() {
			if (this.isTypeOfTool) {
				return this.featureType;
			}
			if (this.isPlacementAddOnMode) {
				return this.serviceSlug;
			}
			return null;
		},
		filterBy() {
			if (this.isTypeOfTool) {
				return { feature_type: FeatureType.Tool, tool_placement: this.featureType };
			}
			if (this.isPlacementAddOnMode) {
				return {
					service: this.appId,
					service_workspace: this.serviceWorkspaceId,
					feature_type: FeatureType.AddOn,
				};
			}
			if (this.featureType) {
				return { feature_type: this.featureType };
			}
			return {};
		},
		appId() {
			return this.app.id;
		},
		app() {
			return this.getServiceBySlug(this.serviceSlug) || {};
		},
		serviceHasContext() {
			return CreateAndManageService.doesServiceHaveContext(this.app);
		},
		isPlacementAddOnMode() {
			return CreateAndManageService.isPlacementAddOnMode(this.$route);
		},
		isTypeOfTool() {
			return Boolean(this.featureType) && Object.keys(TOOL_PLACEMENTS).includes(this.featureType);
		},
		notSupported() {
			if (this.serviceSlug) {
				return !this.serviceHasContext;
			}
			return false;
		},
		canCreateNew() {
			if (this.disableCreateNew) {
				return false;
			}
			if (this.projectToCreateWorkflowIn) {
				return this.projectHasPermission(this.selectedProjectId, ProjectPermissions.ChangeWorkflows);
			}

			return this.hasAccountPermission(AccountPermissions.CreateWorkflows);
		},
		projectToCreateWorkflowIn() {
			return VIRTUAL_PROJECT_IDS.includes(this.selectedProjectId) ? undefined : Number(this.selectedProjectId);
		},
	},
	methods: {
		showWizard() {
			let initialState = {
				projectId: this.projectToCreateWorkflowIn,
			};
			if (this.initialState) {
				initialState = { ...initialState, ...this.initialState };
			} else {
				if (this.isTypeOfTool) {
					initialState = { ...initialState, featureTypeId: FeatureType.Tool, toolPlacement: this.featureType };
				} else if (this.isPlacementAddOnMode) {
					initialState = {
						...initialState,
						appId: this.appId,
						featureTypeId: FeatureType.AddOn,
						serviceWorkspaceId: this.serviceWorkspaceId === UNIDENTIFIED_SERVICE_WORKSPACE_ID ? null : this.serviceWorkspaceId,
					};
				} else {
					initialState = { ...initialState, featureTypeId: this.featureType };
				}
			}

			AnalyticsService.trackButtonClicked(AnalyticsService.BUTTONS.CREATE_NEW_FEATURE, {});

			AppDialogManagerService.openCreationWizardDialog({ initialState });
		},
	},
};
</script>

<style scoped lang="scss"></style>
